import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientById } from '@hooks/usePatientById';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';

import { DateRangeSelector } from '../../../../components/DateRangeSelector';
import PatientPicker from '../../../../components/Patient/PatientPicker';
import type { Patient } from '../../../../types/types';
import { useProvidersList } from '../../../jotaudit/hooks/useProvidersList';
import {
  CLAIM_FILTER_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  READY_TO_BILL_FILTER_OPTIONS,
  STATUS_OPTIONS_BY_TAB,
} from '../../constants/statusOptions';

import type { TabType } from './types';

const DATE_PRESET_DAYS = [1, 7, 14, 30, 90] as const;

/** Format a Date as YYYY-MM-DD in local time */
const formatLocalDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Detect which preset (if any) matches the current from/to range */
const detectActivePreset = (
  dateFrom?: string,
  dateTo?: string,
): number | null => {
  if (!dateFrom || !dateTo) return null;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dateTo !== formatLocalDate(tomorrow)) return null;
  for (const days of DATE_PRESET_DAYS) {
    const from = new Date();
    from.setDate(from.getDate() - days);
    if (dateFrom === formatLocalDate(from)) return days;
  }
  return null;
};

interface BillingFiltersProps {
  tab: TabType;
  patient?: string;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  providers?: string;
  hasClaim?: string;
  readyToBill?: string;
  payerName?: string;
  paymentMethod?: string;
  onFilterChange: (filters: {
    patient?: string;
    from?: string;
    to?: string;
    status?: string;
    providers?: string;
    hasClaim?: string;
    readyToBill?: string;
    payerName?: string;
    paymentMethod?: string;
  }) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
}

export const BillingFilters: React.FC<BillingFiltersProps> = ({
  tab,
  patient,
  dateFrom,
  dateTo,
  status,
  providers,
  hasClaim,
  readyToBill,
  payerName,
  paymentMethod,
  onFilterChange,
  onClearFilters,
  hasActiveFilters,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>(status || '');
  const [selectedHasClaim, setSelectedHasClaim] = useState<string>(hasClaim || '');
  const [selectedReadyToBill, setSelectedReadyToBill] = useState<string>(readyToBill || '');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(paymentMethod || '');
  const [localPayerName, setLocalPayerName] = useState<string>(payerName || '');
  const [expandedMobile, setExpandedMobile] = useState(false);

  // Fetch patient data when we have a patient ID from URL
  const { patient: loadedPatient, loading: loadingPatient } = usePatientById(patient);

  // Fetch providers for multi-select (encounters tab only)
  const { providers: allProviders, isLoading: isLoadingProviders } = useProvidersList();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Update local state when URL params change
  useEffect(() => {
    setSelectedStatus(status || '');
  }, [status]);
  useEffect(() => {
    setSelectedHasClaim(hasClaim || '');
  }, [hasClaim]);
  useEffect(() => {
    setSelectedReadyToBill(readyToBill || '');
  }, [readyToBill]);
  useEffect(() => {
    setSelectedPaymentMethod(paymentMethod || '');
  }, [paymentMethod]);
  useEffect(() => {
    setLocalPayerName(payerName || '');
  }, [payerName]);

  // Provider options for Autocomplete
  const providerOptions = useMemo(
    () =>
      allProviders.map(p => ({
        id: p.user_id,
        label: p.provider_name || p.user_id,
      })),
    [allProviders],
  );

  // Parse selected provider IDs from comma-separated string
  const selectedProviders = useMemo(() => {
    if (!providers) return [];
    const ids = providers.split(',');
    return providerOptions.filter(p => ids.includes(p.id));
  }, [providers, providerOptions]);

  const isRemittancesTab = tab === 'remittances';

  // Shared filter state to pass through
  const getCurrentFilters = () => ({
    patient,
    from: dateFrom,
    to: dateTo,
    status,
    providers,
    hasClaim,
    readyToBill,
    payerName,
    paymentMethod,
  });

  const handlePatientSelect = (p: Patient | undefined) => {
    onFilterChange({
      ...getCurrentFilters(),
      patient: p?.patient_id,
    });
  };

  const activePreset = useMemo(
    () => detectActivePreset(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  const handleDateRangeChange = (range: { start: Date | null; end: Date | null }) => {
    onFilterChange({
      ...getCurrentFilters(),
      from: range.start ? formatLocalDate(range.start) : undefined,
      to: range.end ? formatLocalDate(range.end) : undefined,
    });
  };

  const handleDatePresetClick = useCallback(
    (days: number) => {
      const from = new Date();
      from.setDate(from.getDate() - days);
      const to = new Date();
      to.setDate(to.getDate() + 1); // +1 because backend treats end date as exclusive
      onFilterChange({
        ...getCurrentFilters(),
        from: formatLocalDate(from),
        to: formatLocalDate(to),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFilterChange, patient, dateFrom, dateTo, status, providers, hasClaim, readyToBill],
  );

  const handleStatusChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedStatus(value);
    onFilterChange({
      ...getCurrentFilters(),
      status: value || undefined,
    });
  };

  const handleClearStatus = () => {
    setSelectedStatus('');
    onFilterChange({
      ...getCurrentFilters(),
      status: undefined,
    });
  };

  const handleProvidersChange = (
    _: React.SyntheticEvent,
    value: Array<{ id: string; label: string }>,
  ) => {
    const providerIds = value.map(p => p.id).join(',');
    onFilterChange({
      ...getCurrentFilters(),
      providers: providerIds || undefined,
    });
  };

  const handleHasClaimChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedHasClaim(value);
    onFilterChange({
      ...getCurrentFilters(),
      hasClaim: value || undefined,
    });
  };

  const handleClearHasClaim = () => {
    setSelectedHasClaim('');
    onFilterChange({
      ...getCurrentFilters(),
      hasClaim: undefined,
    });
  };

  const handleReadyToBillChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedReadyToBill(value);
    onFilterChange({
      ...getCurrentFilters(),
      readyToBill: value || undefined,
    });
  };

  const handleClearReadyToBill = () => {
    setSelectedReadyToBill('');
    onFilterChange({
      ...getCurrentFilters(),
      readyToBill: undefined,
    });
  };

  const handlePayerNameSubmit = () => {
    onFilterChange({
      ...getCurrentFilters(),
      payerName: localPayerName || undefined,
    });
  };

  const handlePaymentMethodChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedPaymentMethod(value);
    onFilterChange({
      ...getCurrentFilters(),
      paymentMethod: value || undefined,
    });
  };

  const handleClearPaymentMethod = () => {
    setSelectedPaymentMethod('');
    onFilterChange({
      ...getCurrentFilters(),
      paymentMethod: undefined,
    });
  };

  const handleClearFilters = () => {
    setSelectedStatus('');
    setSelectedHasClaim('');
    setSelectedReadyToBill('');
    setSelectedPaymentMethod('');
    setLocalPayerName('');
    onClearFilters();
  };

  const statusOptions = STATUS_OPTIONS_BY_TAB[tab] || [];
  const isEncountersTab = tab === 'unbilled';
  const showProviderFilter = tab === 'unbilled' || tab === 'claims';
  const showPatientFilter = !isRemittancesTab;
  const showStatusFilter = !isRemittancesTab;

  // Calculate active filter count for mobile badge
  const activeFilterCount = [
    patient ? 1 : 0,
    selectedStatus ? 1 : 0,
    dateFrom || dateTo ? 1 : 0,
    providers ? 1 : 0,
    selectedHasClaim ? 1 : 0,
    selectedReadyToBill ? 1 : 0,
    payerName ? 1 : 0,
    selectedPaymentMethod ? 1 : 0,
  ].reduce((sum, val) => sum + val, 0);

  // Generate filter summary text for mobile
  const getFilterSummary = () => {
    const parts = [];
    if (patient && loadedPatient) {
      const name =
        `${loadedPatient.first_name || ''} ${loadedPatient.last_name || ''}`.trim() || 'Patient';
      parts.push(name);
    } else if (patient) {
      parts.push('Patient');
    }
    if (selectedStatus) {
      const option = statusOptions.find(opt => opt.value === selectedStatus);
      parts.push(option?.label || 'Status');
    }
    if (dateFrom || dateTo) parts.push('Date range');
    if (providers) parts.push('Providers');
    if (selectedHasClaim) parts.push('Claim filter');
    if (selectedReadyToBill) {
      const option = READY_TO_BILL_FILTER_OPTIONS.find(opt => opt.value === selectedReadyToBill);
      parts.push(option?.label || 'Ready to Bill');
    }
    if (payerName) parts.push(`Payer: ${payerName}`);
    if (selectedPaymentMethod) {
      const option = PAYMENT_METHOD_OPTIONS.find(opt => opt.value === selectedPaymentMethod);
      parts.push(option?.label || 'Payment method');
    }
    return parts.length > 0 ? parts.join(', ') : 'No filters';
  };

  // Helper to render a clearable select
  const renderSelect = (
    label: string,
    value: string,
    onChange: (event: SelectChangeEvent) => void,
    onClear: () => void,
    options: ReadonlyArray<{ readonly value: string; readonly label: string; readonly color: string }>,
  ) => (
    <FormControl size="small" fullWidth>
      <InputLabel shrink={!!value}>{label}</InputLabel>
      <Select
        value={value}
        onChange={onChange}
        input={
          <OutlinedInput
            label={value ? label : ''}
            notched={!!value}
            endAdornment={
              value && (
                <InputAdornment position="end" sx={{ mr: 3 }}>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onClear();
                    }}
                    onMouseDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
          />
        }
        displayEmpty
        renderValue={val => {
          if (!val) return '';
          const option = options.find(opt => opt.value === val);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                label={option?.label || val}
                size="small"
                color={(option?.color as 'default' | 'primary' | 'success' | 'info' | 'warning' | 'error') || 'default'}
                sx={{ height: 22 }}
              />
            </Box>
          );
        }}
      >
        <MenuItem value="">
          <Typography color="text.secondary">All</Typography>
        </MenuItem>
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: isMobile && !expandedMobile ? 0.75 : isMobile ? 1.5 : 2,
        mb: 2,
        borderRadius: 1,
        bgcolor: '#fafafa',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack spacing={isMobile && !expandedMobile ? 0 : 2}>
        {/* Mobile collapsed header */}
        {isMobile ? (
          <Box>
            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setExpandedMobile(!expandedMobile);
              }}
              startIcon={
                <Badge badgeContent={activeFilterCount} color="primary">
                  <FilterListIcon />
                </Badge>
              }
              endIcon={expandedMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                justifyContent: 'space-between',
                px: 1,
                py: 0.5,
                color: 'text.primary',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ml: 1 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Filters
                </Typography>
                {activeFilterCount > 0 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {getFilterSummary()}
                  </Typography>
                )}
              </Box>
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Box sx={{ fontWeight: 500, fontSize: '0.875rem', color: 'text.secondary' }}>
              Filters
            </Box>
            {hasActiveFilters && (
              <Chip
                label="Active"
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        )}

        {/* Collapsible filter content */}
        <Collapse in={!isMobile || expandedMobile}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: showProviderFilter ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)',
              },
              gap: 2,
              pt: isMobile ? 1 : 0,
            }}
          >
            {/* Patient Picker (not shown for remittances) */}
            {showPatientFilter && (
              <Box sx={{ minWidth: 200 }}>
                <PatientPicker
                  key={patient || 'no-patient'}
                  onPatientSelect={handlePatientSelect}
                  initialPatient={loadingPatient ? undefined : loadedPatient || undefined}
                  size="small"
                  disableAddNew={true}
                />
              </Box>
            )}

            {/* Status Select (not shown for remittances) */}
            {showStatusFilter && (
              <Box sx={{ minWidth: 160 }}>
                {renderSelect('Status', selectedStatus, handleStatusChange, handleClearStatus, statusOptions)}
              </Box>
            )}

            {/* Date Range Selector + Quick Presets */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <DateRangeSelector
                value={(() => {
                  const parseLocalDate = (dateStr: string) => {
                    const [year, month, day] = dateStr.split('-').map(Number);
                    return new Date(year, month - 1, day);
                  };
                  return {
                    start: dateFrom ? parseLocalDate(dateFrom) : null,
                    end: dateTo ? parseLocalDate(dateTo) : null,
                  };
                })()}
                onChange={handleDateRangeChange}
                size="small"
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {DATE_PRESET_DAYS.map(days => (
                  <Chip
                    key={days}
                    label={`${days}d`}
                    onClick={() => handleDatePresetClick(days)}
                    color={activePreset === days ? 'primary' : 'default'}
                    variant={activePreset === days ? 'filled' : 'outlined'}
                    size="small"
                    sx={{ cursor: 'pointer', minWidth: 40 }}
                  />
                ))}
              </Box>
            </Box>

            {/* Provider Multi-Select (encounters + claims tabs) */}
            {showProviderFilter && (
              <Box sx={{ minWidth: 200 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={providerOptions}
                  value={selectedProviders}
                  onChange={handleProvidersChange}
                  loading={isLoadingProviders}
                  getOptionLabel={option => option.label}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={params => (
                    <TextField {...params} label="Provider" placeholder="All providers" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip key={key} label={option.label} size="small" {...tagProps} />
                      );
                    })
                  }
                />
              </Box>
            )}

            {/* Encounters-specific filters */}
            {isEncountersTab && (
              <>
                {/* Has Claim Filter */}
                <Box sx={{ minWidth: 140 }}>
                  {renderSelect('Claim', selectedHasClaim, handleHasClaimChange, handleClearHasClaim, CLAIM_FILTER_OPTIONS)}
                </Box>

                {/* Ready to Bill — now shown as queue chips above the table */}
              </>
            )}

            {/* Remittances-specific filters */}
            {isRemittancesTab && (
              <>
                {/* Payer Name Search */}
                <Box sx={{ minWidth: 200 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Payer Name"
                    placeholder="Search payers..."
                    value={localPayerName}
                    onChange={e => setLocalPayerName(e.target.value)}
                    onBlur={handlePayerNameSubmit}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handlePayerNameSubmit();
                    }}
                    InputProps={{
                      endAdornment: localPayerName ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setLocalPayerName('');
                              onFilterChange({
                                ...getCurrentFilters(),
                                payerName: undefined,
                              });
                            }}
                            edge="end"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                </Box>

                {/* Payment Method Select */}
                <Box sx={{ minWidth: 160 }}>
                  {renderSelect('Payment Method', selectedPaymentMethod, handlePaymentMethodChange, handleClearPaymentMethod, PAYMENT_METHOD_OPTIONS)}
                </Box>
              </>
            )}
          </Box>
        </Collapse>

        {/* Clear Filters Button */}
        {hasActiveFilters && (!isMobile || expandedMobile) && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              sx={{
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'text.secondary',
                  bgcolor: 'transparent',
                },
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
